/**
 * Based on https://github.com/davidtheclark/tabbable
 */
const CANDIDATE_SELECTOR = [
	"input",
	"select",
	"textarea",
	"a[href]",
	"button",
	"[tabindex]",
	"audio[controls]",
	"video[controls]",
	'[contenteditable]:not([contenteditable="false"])',
].join(",");

function isFocusableCandidate(element) {
	return (
		!element.disabled &&
		!(element.tagName === "INPUT" && element.type === "hidden") &&
		// offsetParent being null will allow detecting cases where an element is invisible or inside an invisible element,
		// as long as the element does not use position: fixed. For them, their visibility has to be checked directly as well.
		!(
			element.offsetParent === null ||
			getComputedStyle(element).visibility === "hidden"
		)
	);
}

function isTabbableRadio(element) {
	if (!element.name) return true;
	// This won't account for the edge case where you have radio groups with the same
	// in separate forms on the same page.
	const radioSet = Array.from(
		element.ownerDocument.querySelectorAll(
			'input[type="radio"][name="' + element.name + '"]',
		),
	);
	const checked = radioSet.find(element => element.checked);
	return !checked || checked === element;
}

function isTabbableCandidate(element) {
	return (
		isFocusableCandidate(element) &&
		(element.tagName !== "INPUT" ||
			element.type !== "radio" ||
			isTabbableRadio(element))
	);
}

function getTabIndex(element) {
	const tabIndexAttr = parseInt(element.getAttribute("tabindex"), 10);
	if (!isNaN(tabIndexAttr)) {
		return tabIndexAttr;
	}
	// Browsers do not return `tabIndex` correctly for contentEditable nodes;
	// so if they don't have a tabindex attribute specifically set, assume it's 0.
	if (element.contentEditable === "true") {
		return 0;
	}
	return element.tabIndex;
}

function splitByTabIndex(candidates) {
	return candidates.reduce(
		(acc, candidate, documentIndex) => {
			const tabIndex = getTabIndex(candidate);
			if (tabIndex < 0) {
				return acc;
			}

			if (!isTabbableCandidate(candidate)) {
				return acc;
			}

			if (tabIndex === 0) {
				acc.withoutTabIndex.push(candidate);
			} else {
				acc.withTabIndex.push({
					documentIndex,
					tabIndex,
					candidate,
				});
			}
			return acc;
		},
		{ withoutTabIndex: [], withTabIndex: [] },
	);
}

export function getTabbables(element) {
	const { withoutTabIndex, withTabIndex } = splitByTabIndex(
		Array.from(element.querySelectorAll(CANDIDATE_SELECTOR)),
	);

	return withTabIndex
		.sort((a, b) => {
			return a.tabIndex === b.tabIndex
				? a.documentIndex - b.documentIndex
				: a.tabIndex - b.tabIndex;
		})
		.map(function (candidateData) {
			return candidateData.candidate;
		})
		.concat(withoutTabIndex);
}
